import { PlayIcon } from '@heroicons/react/24/solid';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { executeDraw, getDrawDetails, getDrawWinners, getLotteryDetails, updateDraw } from '@/api/Lottery';
import { Layout } from '@/components/Layout';
import { LayoutCard } from '@/components/LayoutCard';
import AccentTitle from '@/components/UI/AccentTitle';
import Button from '@/components/UI/Button';
import type { Draw, DrawWinner, Lottery } from '@/types/Lottery';
import EditableField from '../Details/EditableField';

interface ErrorState {
  message: string;
  type: 'error' | 'warning' | 'info';
}

const DrawDetailsPage: React.FC = () => {
  const { lotteryId, drawId } = useParams<{
    lotteryId: string;
    drawId: string;
  }>();
  const [draw, setDraw] = useState<Draw | null>(null);
  const [lottery, setLottery] = useState<Lottery | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExecutingDraw, setIsExecutingDraw] = useState(false);
  const [winners, setWinners] = useState<DrawWinner[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [error, setError] = useState<ErrorState | null>(null);
  const [_isUpdatingDraw, setIsUpdatingDraw] = useState(false);
  const navigate = useNavigate();

  const handleApiError = useCallback((error: any, context: string) => {
    console.error(`Error in ${context}:`, error);

    const errorMessage = (() => {
      if (error?.response) {
        // Server responded with error status
        const { status, data } = error.response;

        switch (status) {
          case 400:
            return data?.message || 'Bad request. Please check your input and try again.';
          case 401:
            return 'You are not authorized to perform this action.';
          case 403:
            return 'Access forbidden. You do not have permission to view this resource.';
          case 404:
            return 'The requested resource was not found.';
          case 409:
            return data?.message || 'Conflict occurred. The resource may have been modified by another user.';
          case 422:
            return data?.message || 'Validation error. Please check your input.';
          case 429:
            return 'Too many requests. Please wait a moment and try again.';
          case 500:
            return 'Server error. Please try again later.';
          case 502:
          case 503:
          case 504:
            return 'Service temporarily unavailable. Please try again later.';
          default:
            return data?.message || `Server error (${status}). Please try again.`;
        }
      }
      if (error?.request) {
        // Network error - no response received
        return 'Network error. Please check your connection and try again.';
      }
      if (error?.message) {
        // Other error with message
        return error.message;
      }
      return 'An unexpected error occurred. Please try again.';
    })();

    setError({ message: errorMessage, type: 'error' });

    // Auto-clear error after 5 seconds
    setTimeout(() => {
      setError(null);
    }, 5000);
  }, []);

  const clearError = () => {
    setError(null);
  };

  useEffect(() => {
    if (drawId && lotteryId) {
      const fetchDetails = async () => {
        try {
          setError(null);
          setIsLoading(true);

          // Fetch draw details
          const drawResponse = await getDrawDetails(drawId);
          const drawData = drawResponse.data.data || drawResponse.data;
          setDraw(drawData);

          // Fetch lottery details
          const lotteryResponse = await getLotteryDetails(lotteryId);
          const lotteryData = lotteryResponse.data.data || lotteryResponse.data;
          setLottery(lotteryData);

          // Fetch winners
          const winnersResponse = await getDrawWinners(drawId);
          const winnersData = winnersResponse.data.data || winnersResponse.data;
          setWinners(winnersData);
        } catch (error) {
          handleApiError(error, 'fetching draw details');
        } finally {
          setIsLoading(false);
        }
      };
      fetchDetails();
    }
  }, [lotteryId, drawId, handleApiError]);

  const handleExecuteDraw = async () => {
    if (!draw || !lottery || !lotteryId || !drawId) return;

    setIsExecutingDraw(true);
    setError(null);

    try {
      await executeDraw(drawId);

      // Refresh the draw data to show the new winners
      const drawResponse = await getDrawDetails(drawId);
      const drawData = drawResponse.data.data || drawResponse.data;
      setDraw(drawData);

      // Refresh the winners list
      const winnersResponse = await getDrawWinners(drawId);
      const winnersData = winnersResponse.data.data || winnersResponse.data;
      setWinners(winnersData);

      // Success feedback
      setError({
        message: 'Draw executed successfully! Winners have been selected.',
        type: 'info',
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setError(null);
      }, 3000);
    } catch (error) {
      handleApiError(error, 'executing draw');
    } finally {
      setIsExecutingDraw(false);
    }
  };

  const handleUpdateDraw = async (name: string, value: string | number | boolean) => {
    if (!drawId) return;

    setIsUpdatingDraw(true);
    setError(null);

    try {
      // Ensure totalWinners is a number if that's the field being updated
      const fieldValue = name === 'totalWinners' ? Number(value) : value;
      const updatedDraw = { [name]: fieldValue };

      const response = await updateDraw(drawId, updatedDraw);
      const updatedDrawData = response.data.data || response.data;
      setDraw(updatedDrawData);

      // Success feedback
      setError({
        message: `${name} updated successfully!`,
        type: 'info',
      });

      // Clear success message after 2 seconds
      setTimeout(() => {
        setError(null);
      }, 2000);
    } catch (error) {
      handleApiError(error, 'updating draw');
      throw error; // Re-throw to be caught by EditableField
    } finally {
      setIsUpdatingDraw(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <LayoutCard>
          <div className='flex justify-center items-center p-8'>
            <div className='text-lg text-gray-600'>Loading...</div>
          </div>
        </LayoutCard>
      </Layout>
    );
  }

  if (!draw || !lottery) {
    return (
      <Layout>
        <LayoutCard>
          <div className='flex justify-center items-center p-8'>
            <div className='text-center'>
              <div className='text-lg text-gray-600 mb-2'>Draw or Lottery not found</div>
              <div className='text-sm text-gray-500 mb-4'>
                The requested resource may have been deleted or you may not have permission to view it.
              </div>
              <Button type='button' label='Go Back to Draws' btnStyle='btn-primary' onClick={() => navigate('/lottery/draws')} />
            </div>
          </div>
        </LayoutCard>
      </Layout>
    );
  }

  const _formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  };

  // Filter winners and non-winners
  const winnersList = winners.filter((winner) => winner.status === 'WINNER');
  const allApplicants = winners; // All applicants including winners and non-winners

  // Pagination logic
  const totalPages = Math.ceil(allApplicants.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentApplicants = allApplicants.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Pagination component
  const Pagination = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return (
      <div className='flex items-center justify-between px-6 py-4 border-t bg-gray-50'>
        <div className='flex items-center gap-4'>
          <span className='text-sm text-gray-700'>
            Showing {startIndex + 1} to {Math.min(endIndex, allApplicants.length)} of {allApplicants.length} results
          </span>
          <div className='flex items-center gap-2'>
            <span className='text-sm text-gray-700'>Items per page:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className='text-sm border border-gray-300 rounded px-2 py-1 bg-white'
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        <div className='flex items-center gap-2'>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className='px-3 py-1 text-sm border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            Previous
          </button>

          <div className='flex items-center gap-1'>
            {pageNumbers.map((page) => (
              <button
                key={`page-${page}`}
                onClick={() => (typeof page === 'number' ? handlePageChange(page) : null)}
                disabled={typeof page !== 'number'}
                className={`px-3 py-1 text-sm border rounded ${
                  page === currentPage
                    ? 'bg-primaryColor-normal text-white border-primaryColor-normal'
                    : typeof page === 'number'
                      ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      : 'bg-white text-gray-400 border-gray-300 cursor-default'
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className='px-3 py-1 text-sm border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className='space-y-6'>
        {/* Error Banner */}
        {error && (
          <div className='fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-2xl'>
            <div
              className={`mx-4 p-4 rounded-lg shadow-lg border ${
                error.type === 'error'
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : error.type === 'warning'
                    ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                    : 'bg-blue-50 border-blue-200 text-blue-800'
              }`}
            >
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <span className='font-medium'>{error.type === 'error' ? 'Error' : error.type === 'warning' ? 'Warning' : 'Info'}</span>
                  <span>{error.message}</span>
                </div>
                <button onClick={clearError} className='text-gray-400 hover:text-gray-600'>
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Draw Information Section */}
        <LayoutCard>
          <div className='p-6'>
            <div className='flex items-center justify-between mb-6'>
              <AccentTitle title='Draw Information' />
              {draw && draw.status?.toLocaleLowerCase() !== 'executed' && (
                <button
                  onClick={handleExecuteDraw}
                  disabled={isExecutingDraw}
                  className='flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  <PlayIcon className='w-4 h-4' />
                  {isExecutingDraw ? 'Executing...' : 'Execute Draw'}
                </button>
              )}
            </div>
            <div className='space-y-4'>
              <div className='space-y-4'>
                <EditableField name='name' label='Draw Name' value={draw.name || ''} onSave={handleUpdateDraw} type='text' />
                <EditableField
                  name='totalWinners'
                  label='Total Winners'
                  value={draw.totalWinners || 0}
                  onSave={handleUpdateDraw}
                  type='number'
                />
                <div className='flex items-center gap-2 pl-6'>
                  <span className='text-sm font-medium text-gray-600'>Status:</span>
                  <span
                    className={`text-sm px-2 py-1 rounded-full ${
                      draw.status === 'executed'
                        ? 'bg-green-100 text-green-800'
                        : draw.status === 'in_progress'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {draw.status === 'executed'
                      ? 'Executed'
                      : draw.status === 'in_progress'
                        ? 'In Progress'
                        : draw.status === 'pending'
                          ? 'Pending'
                          : draw.status || 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </LayoutCard>

        {/* Draw Results Section - Full Width */}
        <LayoutCard>
          <div className='h-full flex flex-col'>
            <div className='p-6 shrink-0'>
              <AccentTitle title='Draw Results' />
              <div className='mt-2 text-sm text-gray-600'>
                {winnersList.length > 0 ? (
                  <span>
                    Showing {winnersList.length} winner
                    {winnersList.length !== 1 ? 's' : ''} out of {allApplicants.length} total applicants
                  </span>
                ) : (
                  <span>No winners selected yet</span>
                )}
              </div>
            </div>
            <div className='flex-1 overflow-hidden'>
              {allApplicants && allApplicants.length > 0 ? (
                <div className='h-full flex flex-col'>
                  <div className='flex-1 overflow-auto'>
                    <table className='w-full'>
                      <thead className='bg-gray-50 sticky top-0'>
                        <tr>
                          <th className='p-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>#</th>
                          <th className='p-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Name</th>
                          <th className='p-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Email</th>
                          <th className='p-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Contact</th>
                          <th className='p-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Status</th>
                          <th className='p-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Date</th>
                        </tr>
                      </thead>
                      <tbody className='bg-white divide-y divide-gray-200'>
                        {currentApplicants.map((applicant, idx) => (
                          <tr key={applicant.id} className={`hover:bg-gray-50 ${applicant.status === 'WINNER' ? 'bg-green-50' : ''}`}>
                            <td className='p-4 whitespace-nowrap'>
                              <span
                                className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-medium ${
                                  applicant.status === 'WINNER' ? 'bg-green-600' : 'bg-gray-400'
                                }`}
                              >
                                {startIndex + idx + 1}
                              </span>
                            </td>
                            <td className='p-4 whitespace-nowrap font-medium text-neutralColor-dark'>{applicant.nominee.name}</td>
                            <td className='p-4 whitespace-nowrap text-neutralColor-medium'>{applicant.nominee.email}</td>
                            <td className='p-4 whitespace-nowrap text-neutralColor-medium'>{applicant.nominee.contactInfo}</td>
                            <td className='p-4 whitespace-nowrap text-neutralColor-medium'>
                              <span
                                className={`text-sm px-2 py-1 rounded-full ${
                                  applicant.status === 'WINNER' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {applicant.status === 'WINNER' ? 'Winner' : 'Not Selected'}
                              </span>
                            </td>
                            <td className='p-4 whitespace-nowrap text-sm text-gray-500'>
                              {new Date(applicant.createdAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination />
                </div>
              ) : (
                <div className='h-full flex items-center justify-center'>
                  <div className='text-center'>
                    <div className='text-lg text-gray-500 mb-2'>No applicants found</div>
                    <div className='text-sm text-gray-400'>Applicants will appear here once they are added to the draw</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </LayoutCard>
      </div>
    </Layout>
  );
};

export default DrawDetailsPage;
